import { cn } from '@/lib/cn';

/**
 * BarraDelCentro — las dos pantallas del Centro de Notificaciones.
 *
 * Vive en `components/` y no dentro de una feature porque **la usan las dos**:
 * `features/comunicaciones` y `features/notificaciones`. Una feature no importa
 * de otra —lo compartido sube aquí—, que es la misma regla por la que los
 * paneles de equipo subieron al migrar Novedades, después de descubrir que
 * había dos copias y ya habían divergido.
 *
 * Son **enlaces**, no pestañas, y la diferencia no es cosmética: cada una es
 * una ruta distinta del `PAGE_MAP` que sustituye la pantalla entera. Un
 * `role="tab"` promete que el contenido cambia dentro de la misma vista y que
 * las flechas se mueven entre ellas; aquí lo que pasa es una navegación.
 *
 * Con esto se cerró un defecto que no se veía leyendo el código y sí en el
 * navegador: `showTab()` del vanilla empezaba con
 *
 *     document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'))
 *
 * y eso alcanzaba **también a estas dos**. `render()` marcaba «Mensajes
 * directos» como activa y `showTab('new')`, llamado a continuación, se lo
 * quitaba. Resultado medido el 2026-09-15: ninguna de las dos aparecía activa
 * nunca, ni siquiera al entrar.
 *
 * Aquí la pestaña activa la decide la ruta, que es el único dato que no puede
 * desincronizarse de dónde estás.
 */
export function BarraDelCentro({ activa }: { activa: 'comunicaciones' | 'mensajes' }) {
  // `#centro-notificaciones` y no `#comunicaciones`: es el alias que usa el
  // enlace del menú lateral (`index.html:389`), y el shell marca el ítem activo
  // priorizando la coincidencia exacta de href. Los dos alias llevan al mismo
  // sitio; este es el que además deja el menú bien marcado.
  const enlaces = [
    { clave: 'comunicaciones' as const, href: '#centro-notificaciones', icono: 'bi-megaphone', texto: 'Comunicaciones' },
    { clave: 'mensajes' as const, href: '#notificaciones', icono: 'bi-envelope', texto: 'Mensajes directos' },
  ];

  return (
    <nav aria-label="Secciones del Centro de Notificaciones" className="mb-5">
      <ul className="-mb-px flex gap-1 overflow-x-auto border-b border-line
                     [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {enlaces.map((e) => {
          const esActiva = e.clave === activa;
          return (
            <li key={e.clave}>
              <a
                href={e.href}
                aria-current={esActiva ? 'page' : undefined}
                className={cn(
                  'group relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap',
                  'px-4 py-2.5 text-3 font-semibold transition-colors',
                  'focus-visible:focus-anillo min-h-11',
                  esActiva ? 'text-brand-ink' : 'text-muted hover:text-ink',
                )}
              >
                <i className={cn('bi', e.icono)} aria-hidden="true" />
                {e.texto}
                {/* El filete se escala con `transform`. Un `border-bottom` que
                    se enciende cambia la altura de la caja y empuja la fila. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute inset-x-2 bottom-0 h-0.5 origin-center rounded-full bg-brand',
                    'transition-transform duration-150 motion-reduce:transition-none',
                    esActiva ? 'scale-x-100' : 'scale-x-0',
                  )}
                />
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
